import * as Yup from "yup";
import pricingValidation, {
  otherDefaultFeesPrettyNames,
} from "../../../components/modals/add-new-application-modal/pricing-validation";
import config from "../../../config";
import {
  calculateSalesTotal,
  getPricingValidation,
  getDualPricingValidation,
} from "./utils/validation";

const surcharge_validation = pricingValidation.pricingValidation.surcharge;
const flat_rate_validation = pricingValidation.pricingValidation.flat_rate;
const ic_plus_validation = pricingValidation.pricingValidation.ic_plus;
const tiered_validation = pricingValidation.pricingValidation.tiered;

export const getDefaultFees = (organizationId) => {
  if (organizationId == config.paymentcloud.PAYCHEX_ORG_ID) {
    return pricingValidation.pricingValidation.paychex_default_fee;
  } else {
    return pricingValidation.pricingValidation.default_fee;
  }
};

export const businessInformationSchema = Yup.object().shape({
  type_of_ownership: Yup.string()
    .nullable()
    .required("* Type of Ownership is required"),
  legal_business_name: Yup.string()
    .nullable()
    .required("* Legal Business Name is required"),
  dba_name: Yup.string().nullable().required("* DBA Name is required"),
  tax_ein: Yup.string().nullable().required("* Tax ID is required"),
  premise_type: Yup.string().nullable().required("* Premise Type is required"),
  ownership_years: Yup.number()
    .nullable()
    .min(0, "Years cannot be negative")
    .test(
      "total-ownership",
      "At least 1 month of ownership is required",
      function (value) {
        const { ownership_months } = this.parent;
        return value > 0 || ownership_months > 0;
      },
    ),
  ownership_months: Yup.number()
    .nullable()
    .min(0, "Months cannot be negative")
    .max(11, "Months must be less than 12")
    .test(
      "total-ownership",
      "At least 1 month of ownership is required",
      function (value) {
        const { ownership_years } = this.parent;
        return value > 0 || ownership_years > 0;
      },
    ),
  business_type: Yup.string()
    .nullable()
    .required("* Business Type is required"),
  tax_exempt: Yup.string()
    .oneOf(["yes", "no"], "Must be either 'yes' or 'no'")
    .nullable(),
  business_phone: Yup.string()
    .nullable()
    .required("* Business Phone is required"),
  business_email: Yup.string()
    .nullable()
    .required("* Business Email is required"),
  business_website: Yup.string().when("premise_type", {
    is: (premise_type) => premise_type == 8,
    then: Yup.string().url().nullable().required("* Business Website is required"),
    otherwise: Yup.string().nullable(),
  }),
  dba_address: Yup.string().nullable().required("* DBA Address is required"),
  dba_city: Yup.string().nullable().required("* DBA City is required"),
  dba_state: Yup.string().nullable().required("* DBA State is required"),
  dba_zip: Yup.string().nullable().required("* DBA Zip is required"),
  location_years: Yup.number().nullable().min(0, "Years cannot be negative"),
  location_months: Yup.number()
    .nullable()
    .min(0, "Months cannot be negative")
    .max(11, "Months must be less than 12"),
  same_as_dba: Yup.string()
    .oneOf(["yes", "no"], "Must be either 'yes' or 'no'")
    .nullable(),
  legal_address: Yup.string()
    .nullable()
    .when("same_as_dba", {
      is: "no",
      then: Yup.string().nullable().required("* Legal Address is required"),
      otherwise: Yup.string().nullable(),
    }),
  legal_city: Yup.string()
    .nullable()
    .when("same_as_dba", {
      is: "no",
      then: Yup.string().nullable().required("* Legal City is required"),
      otherwise: Yup.string().nullable(),
    }),
  legal_state: Yup.string()
    .nullable()
    .when("same_as_dba", {
      is: "no",
      then: Yup.string().nullable().required("* Legal State is required"),
      otherwise: Yup.string().nullable(),
    }),
  legal_zip: Yup.string()
    .nullable()
    .when("same_as_dba", {
      is: "no",
      then: Yup.string().nullable().required("* Legal Zip is required"),
      otherwise: Yup.string().nullable(),
    }),
  legal_phone: Yup.string()
    .nullable()
    .when("same_as_dba", {
      is: "no",
      then: Yup.string().nullable().required("* Legal Phone is required"),
      otherwise: Yup.string().nullable(),
    }),
});

export const contactInfoSchema = Yup.object().shape({
  first_name: Yup.string()
    .matches(/^[\p{L}. ]+$/u, "Only letters, periods, and accents are allowed")
    .trim()
    .required("* First Name is required"),
  last_name: Yup.string()
    .matches(/^[\p{L}. ]+$/u, "Only letters, periods, and accents are allowed")
    .trim()
    .required("* Last Name is required"),
  dba_name: Yup.string().trim().required("* DBA Name is required"),
  dba_street: Yup.string().trim().required("* DBA Address is required"),
  dba_city: Yup.string().trim().required("* DBA City is required"),
  dba_state: Yup.string().trim().required("* DBA State is required"),
  country: Yup.string().required("* Country is required"),
  dba_zip: Yup.string().when("country", {
    is: "en",
    then: Yup.string()
      .trim()
      .matches(/^\d{5}(-\d{4})?$/, "* Invalid Zip Code")
      .required("* DBA Zip is required"),
    otherwise: Yup.string().nullable(),
  }),
  postal_code: Yup.string().when("country", {
    is: "ca",
    then: Yup.string()
      .nullable()
      .required("* DBA Postal Code is required")
      .matches(
        /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/,
        "*Enter a valid Canadian Postal Code",
      ),
    otherwise: Yup.string().nullable(),
  }),
  email: Yup.string()
    .trim()
    .email("* Invalid Email")
    .required("* Business Email is required"),
  mobile: Yup.string()
    .matches(
      /^\(?(\d{3})\)?[- ]?(\d{3})[- ]?(\d{4})$/,
      "* Invalid Phone Number",
    )
    .required("* Business Phone is required"),
  is_merchant_referral: Yup.string()
    .oneOf(["yes", "no"], "Invalid selection")
    .required("* Merchant Referral selection is required"),
  referrer_name: Yup.string().when("is_merchant_referral", {
    is: "yes",
    then: Yup.string().required("* Referrer Business Name is required"),
    otherwise: Yup.string().notRequired(),
  }),
  referrer_mid: Yup.string().when("is_merchant_referral", {
    is: "yes",
    then: Yup.string().required("* Referrer MID is required"),
    otherwise: Yup.string().notRequired(),
  }),
  is_tm: Yup.string()
    .oneOf(["yes", "no"], "Invalid selection")
    .required("* TM Lead selection is required"),
  telemarketer_name: Yup.string().when("is_tm", {
    is: "yes",
    then: Yup.string().required("* Telemarketer's name is required"),
    otherwise: Yup.string().notRequired(),
  }),
});

const reprogrammedHardwareSchema = Yup.object().shape({
  equipment_type: Yup.string()
    .oneOf(["gateway"], "* Invalid equipment type")
    .required("* Equipment type is required"),
  equipment_model: Yup.string()
    .oneOf(["gateway"], "* Invalid equipment model")
    .required("* Equipment model is required"),
  serial_number: Yup.string().required("* Serial number is required"),
  quantity: Yup.number()
    .integer("* Quantity must be an integer")
    .min(1, "* Quantity must be at least 1")
    .required("* Quantity is required"),
  application_type: Yup.string()
    .oneOf(["gateway"], "* Invalid application type")
    .required("* Application type is required"),
  comm_method: Yup.string()
    .oneOf(["ip"], "* Invalid communication method")
    .required("* Communication method is required"),
  auto_batch: Yup.string()
    .oneOf(["yes", "no"], "* Invalid auto batch value")
    .required("* Auto batch is required"),
  auto_batch_time: Yup.string()
    .matches(/^\d{2}:\d{2}$/, "* Invalid time format (HH:MM)")
    .required("* Auto batch time is required"),
  special_instructions: Yup.string(),
});

export const equipmentSchema = {
  equipments: Yup.array().when("var_only", {
    is: (var_only) => !var_only || Object.keys(var_only).length === 0,
    then: Yup.array().min(1, "Add at least 1 equipment"),
    otherwise: Yup.array(),
  }),
};

const getAllDefaultFeeRules = () => {
  return Object.keys(getDefaultFees()).reduce((acc, key) => {
    acc[key] = Yup.lazy((value, context) => {
      const { organizationId } = context;
      const defaultFees = getDefaultFees(organizationId);
      let schema = Yup.number()
        .transform((value, originalValue) => (originalValue === "" ? 0 : value))
        .required(`${otherDefaultFeesPrettyNames[key]} is required`);

      const fee = defaultFees?.[key];

      if (typeof fee?.minimum === "number") {
        schema = schema.min(
          fee.minimum,
          `Please enter a fee of $${fee.minimum.toFixed(2)} or higher.`,
        );
      }

      if (typeof fee?.maximum === "number") {
        schema = schema.max(
          fee.maximum,
          `Please enter a fee of $${fee.maximum.toFixed(2)} or lower.`,
        );
      }

      return schema;
    });

    return acc;
  }, {});
};

export const pricingInfoSchema = Yup.object().shape({
  pricing_model: Yup.string().required("* Pricing Model is required"),

  // Dual Pricing
  dual_pricing_monthly_fee: Yup.lazy((value, context) => {
    const { organizationId } = context;
    const dual_pricing_validation = getDualPricingValidation(organizationId);

    let schema = Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? 0 : value))
      .required("Dual Pricing Monthly Fee is required");

    if (
      typeof dual_pricing_validation?.dual_pricing_monthly_fee?.minimum ===
      "number"
    ) {
      schema = schema.min(
        dual_pricing_validation.dual_pricing_monthly_fee.minimum,
        `Please enter a fee of $${dual_pricing_validation.dual_pricing_monthly_fee.minimum.toFixed(2)} or higher.`,
      );
    }

    if (
      typeof dual_pricing_validation?.dual_pricing_monthly_fee?.maximum ===
      "number"
    ) {
      schema = schema.max(
        dual_pricing_validation.dual_pricing_monthly_fee.maximum,
        `Please enter a fee of $${dual_pricing_validation.dual_pricing_monthly_fee.maximum.toFixed(2)} or lower.`,
      );
    }

    return schema;
  }),

  // surcharge
  surcharge_rate: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .required("Surcharge Rate is required")
    .min(
      surcharge_validation.surcharge_rate.minimum,
      `Please enter a rate of ${surcharge_validation.surcharge_rate.minimum.toFixed(2)}% or higher.`,
    )
    .max(
      surcharge_validation.surcharge_rate.maximum,
      `Please enter a rate of ${surcharge_validation.surcharge_rate.maximum.toFixed(2)}% or lower.`,
    ),

  surcharge_per_transaction: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .required("Per Transaction fee is required")
    .min(
      surcharge_validation.surcharge_per_transaction.minimum,
      `Please enter a fee of $${surcharge_validation.surcharge_per_transaction.minimum.toFixed(2)} or higher.`,
    ),

  surcharge_monthly_fee: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .required("Monthly Fee is required")
    .min(
      surcharge_validation.surcharge_monthly_fee.minimum,
      `Please enter a fee of $${surcharge_validation.surcharge_monthly_fee.minimum.toFixed(2)} or higher.`,
    ),

  //Flat Rate
  flat_rate: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .required("Flat Rate is required")
    .min(
      flat_rate_validation.flat_rate.minimum,
      `Please enter a rate of ${flat_rate_validation.flat_rate.minimum.toFixed(2)}% or higher.`,
    ),

  flat_rate_transaction: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .required("Txn Fee is required")
    .min(
      flat_rate_validation.flat_rate_transaction.minimum,
      `Please enter a fee of $${flat_rate_validation.flat_rate_transaction.minimum.toFixed(2)} or higher.`,
    ),

  flat_rate_amex: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .required("AMEX Flat Rate is required")
    .min(
      flat_rate_validation.flat_rate_amex.minimum,
      `Please enter a rate of ${flat_rate_validation.flat_rate_amex.minimum.toFixed(2)}% or higher.`,
    ),

  flat_rate_amex_transaction: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .required("AMEX Txn Fee is required")
    .min(
      flat_rate_validation.flat_rate_amex_transaction.minimum,
      `Please enter a fee of $${flat_rate_validation.flat_rate_amex_transaction.minimum.toFixed(2)} or higher.`,
    ),

  // IC+
  ic_plus_rate: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .required("IC+ Rate is required")
    .min(
      ic_plus_validation.ic_plus_rate.minimum,
      `Please enter a rate of ${ic_plus_validation.ic_plus_rate.minimum.toFixed(2)}% or higher.`,
    ),

  ic_plus_transaction_fee: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .required("Txn Fee is required")
    .min(
      ic_plus_validation.ic_plus_transaction_fee.minimum,
      `Please enter a fee of $${ic_plus_validation.ic_plus_transaction_fee.minimum.toFixed(2)} or higher.`,
    ),

  ic_plus_amex_rate: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .required("AMEX IC+ Rate is required")
    .min(
      ic_plus_validation.ic_plus_amex_rate.minimum,
      `Please enter a rate of ${ic_plus_validation.ic_plus_amex_rate.minimum.toFixed(2)}% or higher.`,
    ),

  ic_plus_amex_transaction_fee: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .required("AMEX Txn Fee is required")
    .min(
      ic_plus_validation.ic_plus_amex_transaction_fee.minimum,
      `Please enter a fee of $${ic_plus_validation.ic_plus_amex_transaction_fee.minimum.toFixed(2)} or higher.`,
    ),

  // Tiered
  // Qualified Fields
  tiered_qual_rate: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .required("Qualified Rate is required")
    .min(
      tiered_validation.tiered_qual_rate.minimum,
      `Please enter a rate of ${tiered_validation.tiered_qual_rate.minimum.toFixed(2)}% or higher.`,
    ),

  tiered_qual_transaction: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .required("Qualified Txn Fee is required")
    .min(
      tiered_validation.tiered_qual_transaction.minimum,
      `Please enter a fee of $${tiered_validation.tiered_qual_transaction.minimum.toFixed(2)} or higher.`,
    ),

  tiered_qual_debit_rate: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .required("Qualified Debit Rate is required")
    .min(
      tiered_validation.tiered_qual_debit_rate.minimum,
      `Please enter a rate of ${tiered_validation.tiered_qual_debit_rate.minimum.toFixed(2)}% or higher.`,
    ),

  tiered_qual_debit_per_transaction: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .required("Qualified Debit Per Txn fee is required")
    .min(
      tiered_validation.tiered_qual_debit_per_transaction.minimum,
      `Please enter a fee of $${tiered_validation.tiered_qual_debit_per_transaction.minimum.toFixed(2)} or higher.`,
    ),

  // Mid-Qualified Fields
  tiered_mid_qual_rate: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .required("Mid-Qualified Rate is required")
    .min(
      tiered_validation.tiered_mid_qual_rate.minimum,
      `Please enter a rate of ${tiered_validation.tiered_mid_qual_rate.minimum.toFixed(2)}% or higher.`,
    ),

  tiered_mid_qual_transaction: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .required("Mid-Qualified Txn Fee is required")
    .min(
      tiered_validation.tiered_mid_qual_transaction.minimum,
      `Please enter a fee of $${tiered_validation.tiered_mid_qual_transaction.minimum.toFixed(2)} or higher.`,
    ),

  tiered_mid_qual_debit_rate: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .required("Mid-Qualified Debit Rate is required")
    .min(
      tiered_validation.tiered_mid_qual_debit_rate.minimum,
      `Please enter a rate of ${tiered_validation.tiered_mid_qual_debit_rate.minimum.toFixed(2)}% or higher.`,
    ),

  tiered_mid_qual_debit_per_transaction: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .required("Mid-Qualified Debit Per Txn fee is required")
    .min(
      tiered_validation.tiered_mid_qual_debit_per_transaction.minimum,
      `Please enter a fee of $${tiered_validation.tiered_mid_qual_debit_per_transaction.minimum.toFixed(2)} or higher.`,
    ),

  // Non-Qualified Fields
  tiered_non_qual_rate: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .required("Non-Qualified Rate is required")
    .min(
      tiered_validation.tiered_non_qual_rate.minimum,
      `Please enter a rate of ${tiered_validation.tiered_non_qual_rate.minimum.toFixed(2)}% or higher.`,
    ),

  tiered_non_qual_transaction: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .required("Non-Qualified Txn Fee is required")
    .min(
      tiered_validation.tiered_non_qual_transaction.minimum,
      `Please enter a fee of $${tiered_validation.tiered_non_qual_transaction.minimum.toFixed(2)} or higher.`,
    ),

  tiered_non_qual_debit_rate: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .required("Non-Qualified Debit Rate is required")
    .min(
      tiered_validation.tiered_non_qual_debit_rate.minimum,
      `Please enter a rate of ${tiered_validation.tiered_non_qual_debit_rate.minimum.toFixed(2)}% or higher.`,
    ),

  tiered_non_qual_debit_per_transaction: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .required("Non-Qualified Debit Per Txn fee is required")
    .min(
      tiered_validation.tiered_non_qual_debit_per_transaction.minimum,
      `Please enter a fee of $${tiered_validation.tiered_non_qual_debit_per_transaction.minimum.toFixed(2)} or higher.`,
    ),

  // Tiered Rate fields
  // AMEX Tier 1
  amex_tier_one_rate: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .when("tiered_amex_structure", {
      is: "tiered",
      then: Yup.number()
        .required("AMEX Tier 1 Rate is required")
        .min(
          tiered_validation.amex_tier_one_rate.minimum,
          `Please enter a rate of ${tiered_validation.amex_tier_one_rate.minimum.toFixed(2)}% or higher.`,
        ),
      otherwise: Yup.number().notRequired(),
    }),
  amex_tier_one_fee: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .when("tiered_amex_structure", {
      is: "tiered",
      then: Yup.number()
        .required("AMEX Tier 1 Fee is required")
        .min(
          tiered_validation.amex_tier_one_fee.minimum,
          `Please enter a fee of $${tiered_validation.amex_tier_one_fee.minimum.toFixed(2)} or higher.`,
        ),
      otherwise: Yup.number().notRequired(),
    }),

  // AMEX Tier 2
  amex_tier_two_rate: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .when("tiered_amex_structure", {
      is: "tiered",
      then: Yup.number()
        .required("AMEX Tier 2 Rate is required")
        .min(
          tiered_validation.amex_tier_two_rate.minimum,
          `Please enter a rate of ${tiered_validation.amex_tier_two_rate.minimum.toFixed(2)}% or higher.`,
        ),
      otherwise: Yup.number().notRequired(),
    }),
  amex_tier_two_fee: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .when("tiered_amex_structure", {
      is: "tiered",
      then: Yup.number()
        .required("AMEX Tier 2 Fee is required")
        .min(
          tiered_validation.amex_tier_two_fee.minimum,
          `Please enter a fee of $${tiered_validation.amex_tier_two_fee.minimum.toFixed(2)} or higher.`,
        ),
      otherwise: Yup.number().notRequired(),
    }),

  // AMEX Tier 3
  amex_tier_three_rate: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .when("tiered_amex_structure", {
      is: "tiered",
      then: Yup.number()
        .required("AMEX Tier 3 Rate is required")
        .min(
          tiered_validation.amex_tier_three_rate.minimum,
          `Please enter a rate of ${tiered_validation.amex_tier_three_rate.minimum.toFixed(2)}% or higher.`,
        ),
      otherwise: Yup.number().notRequired(),
    }),
  amex_tier_three_fee: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .when("tiered_amex_structure", {
      is: "tiered",
      then: Yup.number()
        .required("AMEX Tier 3 Fee is required")
        .min(
          tiered_validation.amex_tier_three_fee.minimum,
          `Please enter a fee of $${tiered_validation.amex_tier_three_fee.minimum.toFixed(2)} or higher.`,
        ),
      otherwise: Yup.number().notRequired(),
    }),

  // AMEX Tier 4
  amex_tier_four_rate: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .when("tiered_amex_structure", {
      is: "tiered",
      then: Yup.number()
        .required("AMEX Tier 4 Rate is required")
        .min(
          tiered_validation.amex_tier_four_rate.minimum,
          `Please enter a rate of ${tiered_validation.amex_tier_four_rate.minimum.toFixed(2)}% or higher.`,
        ),
      otherwise: Yup.number().notRequired(),
    }),
  amex_tier_four_fee: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .when("tiered_amex_structure", {
      is: "tiered",
      then: Yup.number()
        .required("AMEX Tier 4 Fee is required")
        .min(
          tiered_validation.amex_tier_four_fee.minimum,
          `Please enter a fee of $${tiered_validation.amex_tier_four_fee.minimum.toFixed(2)} or higher.`,
        ),
      otherwise: Yup.number().notRequired(),
    }),

  // AMEX Flat Rate
  tiered_amex_flat_rate: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .when("tiered_amex_structure", {
      is: "flat_rate",
      then: Yup.number()
        .required("AMEX Flat Rate is required")
        .min(
          tiered_validation.tiered_amex_flat_rate.minimum,
          `Please enter a rate of ${tiered_validation.tiered_amex_flat_rate.minimum.toFixed(2)}% or higher.`,
        ),
      otherwise: Yup.number(),
    }),

  tiered_amex_flat_transaction: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .when("tiered_amex_structure", {
      is: "flat_rate",
      then: Yup.number()
        .required("AMEX Flat Transaction Fee is required")
        .min(
          tiered_validation.tiered_amex_flat_transaction.minimum,
          `Please enter a fee of $${tiered_validation.tiered_amex_flat_transaction.minimum.toFixed(2)} or higher.`,
        ),
      otherwise: Yup.number(),
    }),

  // AMEX Interchange Plus
  tiered_amex_ic_plus_rate: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .when("tiered_amex_structure", {
      is: "ic_plus",
      then: Yup.number()
        .required("AMEX IC+ Rate is required")
        .min(
          tiered_validation.tiered_amex_ic_plus_rate.minimum,
          `Please enter a rate of ${tiered_validation.tiered_amex_ic_plus_rate.minimum.toFixed(2)}% or higher.`,
        ),
      otherwise: Yup.number(),
    }),

  tiered_amex_ic_plus_transaction: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .when("tiered_amex_structure", {
      is: "ic_plus",
      then: Yup.number()
        .required("AMEX IC+ Transaction Fee is required")
        .min(
          tiered_validation.tiered_amex_ic_plus_transaction.minimum,
          `Please enter a fee of $${tiered_validation.tiered_amex_ic_plus_transaction.minimum.toFixed(2)} or higher.`,
        ),
      otherwise: Yup.number(),
    }),

  debit_rate: Yup.number()
    .transform((val, origVal) => (origVal === "" ? 0 : val))
    .when("pricing_model", (pricing_model, schema, context) => {
      const organizationId = context?.organizationId;
      const validation = getPricingValidation(pricing_model, organizationId);

      const minRate =
        validation?.[`${pricing_model}_pin_debit_rate`]?.minimum ?? 0;

      return schema
        .required("PIN-based Debit Rate is required")
        .min(
          minRate,
          `Please enter a rate of ${minRate.toFixed(2)}% or higher.`,
        );
    }),

  debit_per_txn: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .when("pricing_model", (pricing_model, schema, context) => {
      const organizationId = context?.organizationId;
      const validation = getPricingValidation(pricing_model, organizationId);

      const minPerTxn =
        validation?.[`${pricing_model}_pin_debit_per_transaction`]?.minimum ??
        0;

      return schema
        .required("PIN-based Debit Per Txn fee is required")
        .min(
          minPerTxn,
          `Please enter a fee of $${minPerTxn.toFixed(2)} or higher.`,
        );
    }),

  ebt: Yup.string()
    .oneOf(["yes", "no"], "EBT is required")
    .required("EBT is required"),

  fnf_number: Yup.number().when("ebt", {
    is: (ebt) => ebt === "yes",
    then: Yup.number()
      .nullable()
      .required("* FNF Number is required")
      .test(
        "len",
        "* FNF Number must contain 7 digits",
        (val) => val?.toString()?.length === 7,
      ),
    otherwise: Yup.number().nullable(),
  }),

  // default fees
  ...getAllDefaultFeeRules(),

  ebt_transactions: Yup.number().when("ebt", (ebt, schema, context) => {
    const { organizationId } = context;
    const defaultFees = getDefaultFees(organizationId);
    const base = schema.transform((value, originalValue) =>
      originalValue === "" ? 0 : value,
    );

    if (ebt !== "yes") return base;

    return base
      .required("EBT Transactions is required")
      .min(
        defaultFees.ebt_transactions.minimum,
        `Please enter a fee of $${defaultFees.ebt_transactions.minimum.toFixed(2)} or higher.`,
      );
  }),
  // NOTE: Re-enable this when we have a way to add multiple reprogrammable hardware
  // reprogram_hardware: Yup.boolean().required("Reprogram Hardware is required"),
  // serial_number: Yup.array().when("reprogram_hardware", {
  //   is: true,
  //   then: Yup.array()
  //     .of(reprogrammedHardwareSchema)
  //     .min(1, "At least one reprogrammed hardware is required"),
  //   otherwise: Yup.array(),
  // }),

  // use_existing_gateway: Yup.boolean().required(
  //   "* Use Existing Gateway is required",
  // ),
  // existing_gateway: Yup.string().when("use_existing_gateway", {
  //   is: true,
  //   then: Yup.string().required("* Existing Gateway is required"),
  // }),

  var_only: Yup.object().nullable().shape({
    type: Yup.string()
      .oneOf(["terminal", "gateway"], "Invalid VAR Only type")
      .nullable()
      .notRequired(), // use notRequired instead of optional
    model: Yup.string().when("type", {
      is: (val) => val === "gateway" || val === "terminal",
      then: (schema) => schema.required("Model is required"),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
    serial: Yup.string().when("type", {
      is: "terminal",
      then: (schema) => schema.required("Serial is required"),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
  }),
});

export const detailsInfoSchema = Yup.object().shape({
  who_to_deploy_equipment: Yup.string()
    .oneOf(["agent", "processor"], "* Invalid who deploys equipment")
    .required("* Who deploys equipment is required"),
  who_to_pay_for_equipment: Yup.string()
    .oneOf(["agent", "merchant"], "* Invalid who pays for equipment")
    .required("* Who pays for equipment is required"),
  who_to_deploy_gateway: Yup.string()
    .oneOf(["agent", "processor"], "* Invalid who deploy gateway")
    .required("* Who deploy gateway is required"),
  billing_sequence: Yup.string()
    .oneOf(["weekly", "daily"], "* Invalid billing sequence")
    .required("* Billing sequence is required"),
  cover_cancellation: Yup.string()
    .oneOf(["yes", "no"], "* Invalid cover cancellation fees value")
    .required("* Cover cancellation fees is required"),
  reimbursement_maximum: Yup.number().when("cover_cancellation", {
    is: (cover_cancellation) => cover_cancellation === "yes",
    then: (schema) => schema.nullable().required("* Reimbursement maximum is required"),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  expedited_funding: Yup.string()
    .oneOf(["yes", "no"], "* Invalid expedited funding value")
    .required("* Expedited funding is required"),
  batch_time: Yup.string()
    .matches(/^(0?[1-9]|1[0-2]):[0-5][0-9] (AM|PM)$/i, "* Invalid time format (HH:MM AM/PM)")
    .required("* Batch time is required"),
  equipment_address: Yup.string()
    .oneOf(
      ["legal_address", "home_address", "dba_address", "agent_address"],
      "* Invalid ship equipment to",
    )
    .required("* Ship equipment to is required"),
  invoice_address: Yup.string()
    .oneOf(
      ["legal_address", "home_address", "dba_address", "agent_address"],
      "* Invalid send invoice to",
    )
    .required("* Send invoice to is required"),
  special_instructions: Yup.string(),
});

export const processorInfoSchema = {
  based_on_mcc: Yup.boolean().when("$isPCAdmin", {
    is: true,
    then: (schema) => schema.required("Based on MCC is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  processors: Yup.array().when(["based_on_mcc", "$isPCAdmin"], {
    is: (based_on_mcc, isPCAdmin) => isPCAdmin && based_on_mcc === false,
    then: (schema) =>
      schema.min(
        1,
        "At least one processor is required if Based on MCC is not selected",
      ),
    otherwise: (schema) => schema.notRequired(),
  }),
};

// Signers Schema
const signerSchema = Yup.object().shape({
  signer_first_name: Yup.string()
    .required("* First Name is required")
    .matches(/^[a-zA-Z]+$/, "* First Name must contain only letters"),
  signer_last_name: Yup.string()
    .required("* Last Name is required")
    .matches(/^[a-zA-Z]+$/, "* Last Name must contain only letters"),
  signer_email: Yup.string()
    .email("Invalid email")
    .required("* Email is required"),
  signer_phone: Yup.string()
    .required("* Phone is required")
    .matches(/^\d{10}$/, "* Phone must be 10 digits"),
  signer_address: Yup.string().required("* Address is required"),
  signer_city: Yup.string().required("* City is required"),
  signer_state: Yup.string().required("* State is required"),
  signer_zip: Yup.string()
    .required("* ZIP is required")
    .matches(/^\d{5}$/, "* ZIP must be 5 digits"),
  signer_country: Yup.string().when("signer_document_type", {
    is: (val) => val === "PASSPORT",
    then: Yup.string().required("* Country is required"),
    otherwise: Yup.string().nullable(),
  }),
  signer_social_security: Yup.string()
    .required("* Social Security is required")
    .matches(/^\d{9}$/, "* Social Security must be 9 digits"),
  signer_drivers_license_number: Yup.string().required(
    "* Driver's License Number is required",
  ),
  signer_drivers_license_state: Yup.string().when("signer_document_type", {
    is: (val) => val === "STATE_ID" || val === "LOCAL_STATE_ID",
    then: Yup.string().required("* Driver's License State is required"),
    otherwise: Yup.string().nullable(),
  }),
  signer_date_of_birth: Yup.string()
    .required("* Date of Birth is required")
    .matches(
      /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/,
      "* Date must be in MM/DD/YYYY format"
    )
    .test(
      "age",
      "* Must be at least 18 years old",
      function(value) {
        if (!value) return true;
        const [month, day, year] = value.split('/').map(Number);
        const birthDate = new Date(year, month - 1, day);
        const today = new Date();
        const age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          return age - 1 >= 18;
        }
        return age >= 18;
      }
    ),
  signer_document_type: Yup.string().required("* Document Type is required"),
  signer_document_country: Yup.string().when("signer_document_type", {
    is: (val) => val === "PASSPORT",
    then: Yup.string().required("* Document Country is required"),
    otherwise: Yup.string().nullable(),
  }),
  signer_ownership_percentage: Yup.string()
    .max(100, "* Ownership Percentage must be less than 100%")
    .required("* Ownership Percentage is required"),
  signer_residence_years: Yup.number()
    .nullable()
    .min(0, "Years cannot be negative")
    .max(99, "Years must be less than 99")
    .test(
      "total-residence",
      "At least 1 month of residence is required",
      function (value) {
        const { signer_residence_months } = this.parent;
        return value > 0 || signer_residence_months > 0;
      },
    ),
  signer_residence_months: Yup.number()
    .nullable()
    .min(0, "Months cannot be negative")
    .max(11, "Months must be less than 12")
    .test(
      "total-residence",
      "At least 1 month of residence is required",
      function (value) {
        const { signer_residence_years } = this.parent;
        return value > 0 || signer_residence_years > 0;
      },
    ),
});

export const personalInformationSchema = Yup.object().shape({
  personal_same_as_dba_legal: Yup.string()
    .nullable()
    .oneOf(["dba", "legal", "no", "business"], "Invalid selection")
    .required("* Selection is required"),
  personal_first_name: Yup.string()
    .nullable()
    .required("* First Name is required"),
  personal_last_name: Yup.string()
    .nullable()
    .required("* Last Name is required"),
  personal_email: Yup.string()
    .email("Invalid email")
    .nullable()
    .required("* Email is required"),
  personal_phone: Yup.string()
    .nullable()
    .when("personal_same_as_dba_legal", {
      is: (val) => val === "no",
      then: Yup.string().nullable().required("* Phone is required"),
      otherwise: Yup.string().nullable(),
    }),
  field_personalstreet: Yup.string()
    .nullable()
    .required("* Street Address is required"),
  field_personalcity: Yup.string().nullable().required("* City is required"),
  field_personalstate: Yup.string().nullable().required("* State is required"),
  field_personalzip: Yup.string().nullable().required("* ZIP is required"),
  field_residenceyears: Yup.number()
    .nullable()
    .min(0, "Years cannot be negative")
    .max(99, "Years must be less than 99")
    .test(
      "total-residence",
      "At least 1 month of residence is required",
      function (value) {
        const { field_residencemonths } = this.parent;
        return value > 0 || field_residencemonths > 0;
      },
    ),
  field_residencemonths: Yup.number()
    .nullable()
    .min(0, "Months cannot be negative")
    .max(11, "Months must be less than 12")
    .test(
      "total-residence",
      "At least 1 month of residence is required",
      function (value) {
        const { field_residenceyears } = this.parent;
        return value > 0 || field_residenceyears > 0;
      },
    ),
  field_documenttype: Yup.string()
    .nullable()
    .required("* Document Type is required"),
  field_documentnumber: Yup.string()
    .nullable()
    .required("* Document Number is required"),
  document_country: Yup.string()
    .nullable()
    .when("field_documenttype", {
      is: (val) => val === "PASSPORT",
      then: Yup.string().nullable().required("* Document Country is required"),
      otherwise: Yup.string().nullable(),
    }),
  field_driverstate: Yup.string()
    .nullable()
    .when("field_documenttype", {
      is: (val) => val === "STATE_ID" || val === "LOCAL_STATE_ID",
      then: Yup.string()
        .nullable()
        .required("* Driver's License State is required"),
      otherwise: Yup.string().nullable(),
    }),
  field_ownershippercentage: Yup.mixed()
    .nullable()
    .transform((value) => {
      // Convert empty strings to null
      if (value === "") return null;
      // Return the value as is (could be a string representing a number)
      return value;
    })
    .required("* Ownership Percentage is required"),
  field_socialsecurity: Yup.string()
    .nullable()
    .required("* Social Security Number is required"),
  field_birthdate: Yup.string()
    .nullable()
    .required("* Date of Birth is required")
    .matches(
      /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/,
      "* Date must be in MM/DD/YYYY format"
    )
    .test(
      "age",
      "* Must be at least 18 years old",
      function(value) {
        if (!value) return true;
        const [month, day, year] = value.split('/').map(Number);
        const birthDate = new Date(year, month - 1, day);
        const today = new Date();
        const age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          return age - 1 >= 18;
        }
        return age >= 18;
      }
    ),
  additional_owners: Yup.array().of(signerSchema),
});

export const businessProfileSchema = Yup.object().shape({
  product_service_description: Yup.string()
    .nullable()
    .required("* Product/Service Description is required"),
  prod_advertise_description: Yup.array()
    .of(Yup.string())
    .min(1, "* At least one advertising method is required"),
  is_thirdparty_involved: Yup.string()
    .oneOf(["yes", "no"])
    .required("* Third Party Involvement is required"),
  thirdparty_involved_description: Yup.string().when("is_thirdparty_involved", {
    is: "yes",
    then: Yup.string().required("* Third Party Description is required"),
    otherwise: Yup.string().nullable(),
  }),
  pci_complaint: Yup.string()
    .oneOf(["yes", "no"])
    .required("* PCI Compliance is required"),
  terminated_as_vmdax: Yup.string()
    .oneOf(["yes", "no"])
    .required("* VMDAX Termination is required"),
  prior_data_compromise_investigation: Yup.string()
    .oneOf(["yes", "no"])
    .required("* Prior Data Compromise is required"),
  identified_visa_risk_program: Yup.string()
    .oneOf(["yes", "no"])
    .required("* Visa Risk Program is required"),
  visa_risk_program_desc: Yup.string().when("identified_visa_risk_program", {
    is: "yes",
    then: Yup.string().required("* Visa Risk Program Description is required"),
    otherwise: Yup.string().nullable(),
  }),
  is_multiple_locations: Yup.string()
    .oneOf(["yes", "no"])
    .required("* Multiple Locations is required"),
  multi_merchant: Yup.string().nullable(),
  multi_merchant_of: Yup.string().nullable(),
  additional_location: Yup.string().nullable(),
  additional_location_of: Yup.string().nullable(),
});

const SALES_CATEGORY_FIELDS = [
  "online_sales",
  "in_store_sales",
  "on_the_go_sales",
  "phone_order_sales",
  "mail_order_sales",
];
const SALES_TYPE_FIELDS = ["sales_swiped", "sales_keyed"];

export const creditCardProcessingSchema = Yup.object().shape({
  currently_accept_vdmax: Yup.string()
    .nullable()
    .oneOf(["yes", "no"], "Please select an option")
    .required("* Please indicate if you currently accept credit cards"),

  // Sales percentage validation - total should be 100%
  internet: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .nullable()
    .min(0, "Value cannot be negative")
    .test("sales-total", "Sales percentages must add up to 100%", function () {
      const values = this.parent;
      const totalSales = calculateSalesTotal(values, [
        "internet",
        "on_premise_sale",
        "off_premise_sale",
        "telephone_order",
        "mail_order",
      ]);
      return Math.abs(totalSales - 100) < 0.01; // Allow for small floating point differences
    }),

  on_premise_sale: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .nullable()
    .min(0, "Value cannot be negative"),

  off_premise_sale: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .nullable()
    .min(0, "Value cannot be negative"),

  telephone_order: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .nullable()
    .min(0, "Value cannot be negative"),

  mail_order: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .nullable()
    .min(0, "Value cannot be negative"),

  // Sales type validation - total should be 100%
  sales_swiped: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .nullable()
    .min(0, "Value cannot be negative")
    .test(
      "sales-type-total",
      "Sales type percentages must add up to 100%",
      function () {
        const values = this.parent;
        const totalSales = calculateSalesTotal(values, [
          "sales_swiped",
          "sales_keyed",
        ]);
        return Math.abs(totalSales - 100) < 0.01; // Allow for small floating point differences
      },
    ),

  sales_keyed: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .nullable()
    .min(0, "Value cannot be negative"),

  // Processing amounts - required
  monthly_max_sales: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value,
    )
    .nullable()
    .required("* Max Monthly Sales is required")
    .min(0, "Value cannot be negative"),

  avg_sale_amount: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value,
    )
    .nullable()
    .required("* Average Sale Amount is required")
    .min(0, "Value cannot be negative"),

  high_sale_amount: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value,
    )
    .nullable()
    .required("* Highest Sale Amount is required")
    .min(0, "Value cannot be negative")
    .test(
      "highest-sale",
      "Highest sale cannot be less than average sale",
      function (value) {
        const { avg_sale_amount } = this.parent;
        return !avg_sale_amount || !value || value >= avg_sale_amount;
      },
    ),
  bank_routing: Yup.string()
    .required("* Routing Number is required")
    .matches(
      /^\d{3}-\d{3}-\d{3}$/,
      "* Routing Number must be in format XXX-XXX-XXX",
    )
    .test("is-valid-routing", "* Routing Number must be 9 digits", (value) => {
      if (!value) return true;
      const digits = value.replace(/-/g, "");
      return digits.length === 9;
    }),
  bank_account: Yup.string().required("* Account Number is required"),
  bank_name: Yup.string().required("* Bank Name is required"),
});

// Complete Form Schema
export const formSchema = Yup.object().shape({
  ...businessInformationSchema.fields,
  ...contactInfoSchema.fields,
  ...pricingInfoSchema.fields,
  ...detailsInfoSchema.fields,
  ...processorInfoSchema,
  ...personalInformationSchema.fields,
  ...businessProfileSchema.fields,
  ...creditCardProcessingSchema.fields,
  signers: Yup.array().of(signerSchema),
});

// template form schema
export const templateFormSchema = Yup.object().shape({
  name: Yup.string()
    .required("* Template Name is required"),
  mcc_id: Yup.string(),
  ems_office_id: Yup.string(),
});